import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import Layout from '../../components/Layout';
import DataTable from 'react-data-table-component';
import '../../styles/datatable.css';
import * as XLSX from 'xlsx-js-style';
import Moment from 'moment';
import { Download } from 'lucide-react';

function SuppliersList() {
  
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch suppliers
  const fetchSuppliers = () => {
    setLoading(true);
    axios.post('/list/suppliers/paginated', {
      status_filter: statusFilter
    })
      .then(res => {

        if (res.data && res.data.status === 200) {
          const suppliersArray = res.data.data;

          setSuppliers(Array.isArray(suppliersArray) ? suppliersArray : []);
        } else {

          setSuppliers([]);
        }
        setLoading(false);
      })
      .catch(err => {


        setSuppliers([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSuppliers();
  }, [statusFilter]);

  const getStatusBadge = (status) => {
    if (status === 0) {
      return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>;
    }
    return <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Inactive</span>;
  };

  const getPKPBadge = (pkp) => {
    if (pkp === 1) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">PKP</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Non-PKP</span>;
  };

  // Custom styles for DataTable
  const customStyles = {
    table: {
      style: {
        backgroundColor: 'white',
        borderRadius: '12px',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f3f4f6',
        borderBottomWidth: '2px',
        borderBottomColor: '#e5e7eb',
        minHeight: '52px',
      },
    },
    headCells: {
      style: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#374151',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        fontSize: '12px',
        minHeight: '48px',
        '&:hover': {
          backgroundColor: '#f9fafb',
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
  };

  // DataTable columns
  const columns = [
    {
      name: 'Company Name',
      selector: row => row.companyname,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Type',
      selector: row => row.companytype,
      sortable: true,
      width: '120px',
      cell: row => row.companytype || '-',
    },
    {
      name: 'Contact',
      selector: row => row.email,
      sortable: true,
      grow: 2,
      cell: row => (
        <div>
          <div className="text-sm">{row.email}</div>
          <div className="text-xs text-gray-500">{row.telephoneno}</div>
        </div>
      ),
    },
    {
      name: 'Business',
      selector: row => row.mainbusiness,
      sortable: true,
      width: '150px',
      cell: row => row.mainbusiness || '-',
    },
    {
      name: 'PKP',
      selector: row => row.companystatus,
      sortable: true,
      width: '100px',
      cell: row => getPKPBadge(row.companystatus),
    },
    {
      name: 'Status',
      selector: row => row.supplierstatus,
      sortable: true,
      width: '100px',
      cell: row => getStatusBadge(row.supplierstatus),
    },
  ];

  // Filter suppliers based on search text
  const filteredSuppliers = suppliers.filter(supplier => 
    supplier.companyname?.toLowerCase().includes(filterText.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(filterText.toLowerCase()) ||
    supplier.telephoneno?.toLowerCase().includes(filterText.toLowerCase()) ||
    supplier.mainbusiness?.toLowerCase().includes(filterText.toLowerCase()) ||
    supplier.salesman?.toLowerCase().includes(filterText.toLowerCase())
  );

  // Function to export suppliers to Excel
  const exportToExcel = () => {
    // Create title
    const title = 'Detailed Supplier List';
    const exportDate = Moment().format('MMMM DD, YYYY');
    const subtitle = `Total Suppliers: ${filteredSuppliers.length} | Filters: ${statusFilter ? (statusFilter === '0' ? 'Active Only' : 'Inactive Only') : 'All Status'}`;

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Add title rows
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [[exportDate]], { origin: 'A4' });
    XLSX.utils.sheet_add_aoa(ws, [[subtitle]], { origin: 'A5' });

    // Table headers (row 7)
    const headers = ['Company Name', 'Company Type', 'Email', 'Phone', 'Main Business', 'Salesman', 'PKP Status', 'Supplier Status'];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A7' });

    // Data rows
    const dataRows = filteredSuppliers.map(supplier => [
      supplier.companyname || '',
      supplier.companytype || '-',
      supplier.email || '-',
      supplier.telephoneno || '-',
      supplier.mainbusiness || '-',
      supplier.salesman || '-',
      supplier.companystatus === 1 ? 'PKP' : 'Non-PKP',
      supplier.supplierstatus === 0 ? 'Active' : 'Inactive'
    ]);

    XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });

    // Set column widths
    ws['!cols'] = [
      { wch: 35 }, // Company Name
      { wch: 15 }, // Type
      { wch: 30 }, // Email
      { wch: 20 }, // Phone
      { wch: 25 }, // Main Business
      { wch: 25 }, // Salesman
      { wch: 15 }, // PKP Status
      { wch: 15 }  // Supplier Status
    ];

    // Apply borders and styling
    const borderStyle = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    const totalRow = dataRows.length + 7;
    for (let R = 6; R <= totalRow; R++) {
      for (let C = 0; C <= 7; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: '' };
        }
        if (!ws[cellAddress].s) {
          ws[cellAddress].s = {};
        }
        
        ws[cellAddress].s.border = borderStyle;
        ws[cellAddress].s.alignment = { 
          vertical: 'center', 
          horizontal: 'left',
          wrapText: true 
        };

        // Style header row (row 7 = index 6)
        if (R === 6) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { 
            patternType: 'solid',
            fgColor: { rgb: 'E0E0E0' } 
          };
          ws[cellAddress].s.alignment = { 
            vertical: 'center', 
            horizontal: 'center',
            wrapText: true 
          };
        }
      }
    }

    // Style title rows
    ['A3', 'A4', 'A5'].forEach((cell, idx) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: idx === 0, sz: [14, 12, 11][idx] },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Suppliers List');
    const fileName = `Suppliers_List_${Moment().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Layout title="Suppliers List" subtitle="All registered suppliers">
      <div className="p-4 sm:p-6 md:p-8">
        
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Suppliers Directory</h1>
            <button 
              onClick={exportToExcel}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center space-x-2"
              title="Export to Excel"
            >
              <Download className="w-4 h-4" />
              <span>Export to Excel</span>
            </button>
          </div>
          
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search suppliers..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="">All Status</option>
              <option value="0">Active</option>
              <option value="1">Inactive</option>
            </select>
            
            <button
              onClick={() => {setFilterText(''); setStatusFilter(''); }}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* DataTable */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredSuppliers}
            pagination
            paginationPerPage={20}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              </div>
            }
            noDataComponent={
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg mb-2">No suppliers found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            }
            highlightOnHover
            pointerOnHover
            responsive
            dense
          />
        </div>
      </div>
    </Layout>
  );
}

export default SuppliersList;
